/**
 * @file index.tsx
 *
 * @fileoverview Renders a multi element select component using the downshift library.
 */

import { getSelectOptionText } from "./../../../01-forms/05-select";
import { FormItem } from "./../../../01-forms/11-form-item";
import { FormLabel } from "./../../../01-forms/11-form-item/01-form-label";
import { FormDescription } from "./../../../01-forms/11-form-item/02-form-description";
import { InlineError } from "../../09-inline-error";
import CloseIcon from "@jq-icons/Close";
import Downshift from "downshift";
import { matchSorter } from "match-sorter";
import React, { useState, FocusEvent, ReactElement, forwardRef } from "react";
import { Item } from "./sub-elements/item";
import styles from "./multiple.module.scss";
import { SelectItem } from "../../../../../models/forms";
import { useDebounce } from "../../../../../hooks/useDebounce";

export const MAX_TERM_NAME_LENGTH = 255;

const parentStyle = {
  width: "100%",
};

interface Props {
  /**
   * The id for this select. It will be used for accessibility purposes.
   */
  id?: string;
  /**
   * The name attribute.
   */
  name?: string;
  /**
   * Marks if a field is in an error state.
   *
   * @default false
   */
  error?: boolean;
  /**
   * Placeholder. The text displayed inside the input of the select box.
   *
   * @default "Search something here..."
   */
  placeholder?: string;
  /**
   * The values selected in the select widget.
   *
   * @default []
   */
  values: string[];
  /**
   * All possible items (the options) that are available inside the widget.
   *
   * @default []
   */
  items: SelectItem[];
  /**
   * The label for the widget.
   */
  label?: string | ReactElement;
  /**
   * Description for this field.
   */
  description?: string;
  /**
   * If the element is required. Will map to the "required" HTML5 property.
   */
  required?: boolean;
  /**
   * Show suggestions when you click in the field.
   *
   * @default true
   */
  showSuggestionsOnClick?: boolean;
  /**
   * The event to fire when something changes in the widget.
   */
  onChange?(items: string[], freeItems?: string[]): any;
  /**
   * The event to fire when something changes in the widget.
   */
  onInputChange?(items: string): any;
  /**
   * The event to fire when the user clicks outside the widget.
   */
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
  /**
   * The selected mark text.
   *
   * @default 'Selected'
   */
  selectedMark?: string;
  /**
   * If the field has free items.
   *
   * @default "false"
   */
  hasFreeItems?: boolean;
  /**
   * The text for the button that will add a free term.
   */
  freeItemsAddButtonText?: string;
  /**
   * The error message shown when a free term exceeds 255 characters.
   */
  freeItemsMaxLengthErrorText?: string;
}

export const MultipleAutocomplete = forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      name,
      error = false,
      placeholder = "Search something here...",
      values,
      items = [],
      label,
      description,
      required = false,
      showSuggestionsOnClick = true,
      onChange,
      onInputChange,
      onBlur,
      selectedMark = "Selected",
      hasFreeItems = false,
      freeItemsAddButtonText = "Add new term",
      freeItemsMaxLengthErrorText = "Term names must be 255 characters or fewer.",
    },
    ref
  ) => {
    const [freeItemError, setFreeItemError] = useState(false);
    const [input, setInput] = useState("");
    const [freeItems, setFreeItems] = useState<string[]>([]);
    const [addFreeTermButtonShown, setAddFreeTermButtonShown] = useState(false);

    const debouncedInput = useDebounce(input, 200);

    const handleKeyDown = React.useCallback(
      (evt: any) => {
        if (onChange && values.length && !input.length && evt.keyCode === 8) {
          onChange(values.slice(0, values.length - 1), freeItems);
        }
      },
      [onChange, values, input.length, freeItems]
    );

    const removeItemFromSelection = React.useCallback(
      (value: any) => {
        if (values.includes(value)) {
          const newValues = values.filter((v) => v !== value);
          onChange && onChange(newValues, freeItems);
        } else {
          const newFreeItems = freeItems.filter((i) => i !== value);
          setFreeItems(newFreeItems);
          onChange && onChange(values, newFreeItems);
        }
      },
      [values, freeItems, onChange]
    );

    const handleInputChange = (evt: any) => {
      setFreeItemError(false);
      setInput(evt.target.value);
      onInputChange && onInputChange(evt.target.value);

      if (hasFreeItems) {
        if (!items.map((i) => i.name).includes(evt.target.value)) {
          setAddFreeTermButtonShown(true);
        } else {
          setAddFreeTermButtonShown(false);
        }
      }
    };

    const handleChange = (item: SelectItem | null) => {
      if (onChange && item) {
        React.startTransition(() => {
          onChange([...values, item.code], freeItems);
        });
        setFreeItemError(false);
        setInput("");
      }
    };

    const handleAddFreeTermClick = React.useCallback(() => {
      if (input.trim().length > MAX_TERM_NAME_LENGTH) {
        setFreeItemError(true);
        return;
      }
      setFreeItemError(false);
      const newFreeItems = [...freeItems, input];
      setFreeItems(newFreeItems);
      setAddFreeTermButtonShown(false);
      setInput("");
      onChange && onChange(values, newFreeItems);
    }, [freeItems, input, onChange, values]);

    const getItemNameFromSelectedItem = React.useMemo(
      () =>
        (key: string): string | null => {
          const item = items.find((item) => item.code === key);
          return item ? item.name : key;
        },
      [items]
    );

    const itemNames: string[] = React.useMemo(() => {
      return debouncedInput.length
        ? matchSorter(
            items.map((i) => i.name),
            debouncedInput,
            {threshold: matchSorter.rankings.CONTAINS}
          )
        : items.map((i) => i.name);
    }, [debouncedInput, items]);

    const filteredItems: SelectItem[] = React.useMemo(() => {
      return items.filter((item) => {
        return itemNames.includes(item.name);
      });
    }, [items, itemNames]);

    const indices = React.useMemo(
      () => mapItemIndex(filteredItems, values),
      [filteredItems, values]
    );

    return (
      <FormItem>
        {label && (
          <FormLabel error={error || freeItemError} required={required} htmlFor={id}>
            {label}
          </FormLabel>
        )}
        <Downshift
          inputValue={input}
          onChange={handleChange}
          selectedItem={
            hasFreeItems && freeItems.length > 0
              ? [...values, ...freeItems]
              : values
          }
          itemToString={(item) => (item ? item.code : "")}
          labelId={id}
        >
          {({
            getInputProps,
            getItemProps,
            getRootProps,
            getLabelProps,
            openMenu,
            highlightedIndex,
            isOpen,
            selectedItem,
          }) => (
            <div style={parentStyle}>
              <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                className={styles.inputWrapper}
                style={{
                  borderColor: error || freeItemError
                    ? `var(--color-danger)`
                    : `var(--select-border-color)`,
                }}
                onClick={() => {
                  if (showSuggestionsOnClick) {
                    openMenu();
                  }
                }}
              >
                {(selectedItem as any)?.map((value: string) => (
                  <span // eslint-disable-line jsx-a11y/no-static-element-interactions
                    className={styles.spanElement}
                    key={value}
                    onClick={() => {
                      removeItemFromSelection(value);
                    }}
                  >
                    {getItemNameFromSelectedItem(value)}
                    &nbsp;
                    <CloseIcon title="Close" />
                  </span>
                ))}
                <input
                  className={styles.downshiftInput}
                  {...getInputProps({
                    placeholder,
                    name,
                    id: id,
                    "aria-labelledby": id,
                    "aria-label": label,
                    onChange: handleInputChange,
                    onKeyDown: handleKeyDown,
                    onBlur,
                    ref,
                  })}
                />
              </div>

              {isOpen && (
                <div className={styles.itemsWrapper}>
                  {filteredItems.map((item) => {
                    const selected = values.indexOf(item.code) !== -1;

                    const props = selected
                      ? {}
                      : getItemProps({
                          item,
                          index: indices[item.code],
                        });

                    return (
                      <Item
                        {...props}
                        isActive={highlightedIndex === indices[item.code]}
                        key={item.code}
                      >
                        {getSelectOptionText(item)}
                        {selected && (
                          <span key={item.code} className={styles.itemLabel}>
                            {selectedMark}
                          </span>
                        )}
                      </Item>
                    );
                  })}
                </div>
              )}
              {hasFreeItems && addFreeTermButtonShown && (
                <button
                  type="button"
                  className={styles.freeTermsButton}
                  onClick={handleAddFreeTermClick}
                >
                  {freeItemsAddButtonText}
                </button>
              )}
            </div>
          )}
        </Downshift>
        {freeItemError && <InlineError message={freeItemsMaxLengthErrorText} />}
        {description && <FormDescription>{description}</FormDescription>}
      </FormItem>
    );
  }
);

/**
 * Get the real index of an item.
 *
 * It does this filtering out selected values, and mapping the values to the index.
 * We're doing so that Downshift doesn't recognize selected item,
 * thus won't highlight the selected item.
 *
 * Given that ['Black', 'Blue', 'Green'], and 'Blue' is selected
 * Output: { 'Black': 0, 'Green': 1 }
 *
 * @param {SelectItem[]} items items
 * @param {String[]} values selected items
 * @return {Object} Mapping of selected items to their indices
 */
function mapItemIndex(items: SelectItem[], values: string[]) {
  return items
    .filter((item) => values.indexOf(item.code) === -1)
    .reduce((prev, next, i) => {
      prev[next.code] = i;
      return prev;
    }, {});
}

export default MultipleAutocomplete;
